"use client";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Menu, Tooltip } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";

import { ConfirmationDialog } from "@/components/molecules/ConfirmationDialog";
import { CATEGORIES_PERMISSIONS } from "@/constants/modules.permissions";
import { MAX_CATEGORY_HIERARCHY_LEVEL } from "@/constants/products.constants";
import ROUTE_URLS from "@/constants/route-urls";
import SWR_KEYS from "@/constants/swr-keys";
import useLanguage from "@/contexts/language-context";
import useRemoveCategory from "@/hooks/requests/category/use-remove-category";
import useHasNoPermission from "@/hooks/use-check-permissions";
import useDialog from "@/hooks/use-dialog";
import useMutate from "@/hooks/use-mutate";
import ICategory from "@/interfaces/product.category.interfaces";
import { ProductCategoriesTableMessages } from "@/messages/product-categories/product-categories-table.messages";
import NotificationItem from "@/mui/Items/NotificationItem";
import { buildCreateSubcategoryUrl } from "@/utils/category-url-builder";

interface Props {
  isOpen: boolean;
  anchorEl: (EventTarget & HTMLButtonElement) | null;
  onClose: () => void;
  messages: ProductCategoriesTableMessages;
  selected: ICategory;
  categories: ICategory[] | null;
}

export function Popover({ isOpen, anchorEl, onClose, messages, selected, categories }: Props) {
  const router = useNavigate();
  const { lang } = useLanguage();
  const { isVisible, handleOpen, handleClose } = useDialog<ICategory>();
  const { remove, isLoading: isLoadingRemoveCategory } = useRemoveCategory();
  const { mutate } = useMutate();
  const _id = selected.id;

  const hasNoPermissionToCreate = useHasNoPermission(CATEGORIES_PERMISSIONS.CREATE);
  const hasNoPermissionToEdit = useHasNoPermission(CATEGORIES_PERMISSIONS.EDIT);
  const hasNoPermissionToDelete = useHasNoPermission(CATEGORIES_PERMISSIONS.DELETE);

  const onSuccess = () => {
    mutate(SWR_KEYS.getCategories);
    onClose();
    handleClose();
  };

  const handleConfirmDelete = async () => {
    const successMessage = messages.deleteCategorySuccess;
    await remove({ _id, onSuccess, successMessage });
  };

  const hasChildren = (category: ICategory, categories: ICategory[] | null): boolean => {
    if (!categories) return false;
    return categories.some((cat) => cat.parentCode === category.code);
  };

  const canCreateSubcategory = selected.level < MAX_CATEGORY_HIERARCHY_LEVEL;
  const disableDelete = hasChildren(selected, categories);

  const tooltipHasChildren = disableDelete ? messages.disableDeleteTooltipHasChildren : "";
  const noPermission = hasNoPermissionToDelete ? messages.disableDeleteTooltipNoPermission : "";
  const tooltipMaxLevel = !canCreateSubcategory
    ? `Limite máximo de hierarquia atingido (${MAX_CATEGORY_HIERARCHY_LEVEL} níveis)`
    : "";
  const tooltipNoCreatePermission = hasNoPermissionToCreate
    ? "Sem permissão para criar subcategorias"
    : "";

  const tooltipMessage = tooltipHasChildren || noPermission;
  const tooltipCreateMessage = tooltipMaxLevel || tooltipNoCreatePermission;

  const handleCreateSubcategory = () => {
    const createUrl = buildCreateSubcategoryUrl({
      parentCode: selected.code,
      parentDescription: selected.description,
      language: lang,
    });
    router(createUrl);
    onClose();
  };

  return (
    <Menu
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
    >
      <Tooltip
        title={tooltipCreateMessage}
        arrow
        placement="bottom"
        disableHoverListener={canCreateSubcategory && !hasNoPermissionToCreate}
      >
        <span>
          <NotificationItem
            icon={<AddIcon />}
            onClick={handleCreateSubcategory}
            title="Criar subcategoria"
            disabled={!canCreateSubcategory || hasNoPermissionToCreate}
          />
        </span>
      </Tooltip>

      <NotificationItem
        icon={<EditIcon />}
        onClick={() => router(ROUTE_URLS.updateProductCategory(lang, selected.id))}
        title={messages.updatePopover}
        disabled={hasNoPermissionToEdit}
      />

      <Tooltip
        title={tooltipMessage}
        arrow
        placement="bottom"
        disableHoverListener={!disableDelete && !hasNoPermissionToDelete}
      >
        <span>
          <NotificationItem
            icon={<DeleteIcon />}
            onClick={() => handleOpen(selected)}
            title={messages.removePopover}
            disabled={disableDelete || hasNoPermissionToDelete}
          />
        </span>
      </Tooltip>

      <ConfirmationDialog
        isOpen={isVisible}
        onClose={handleClose}
        onConfirm={() => handleConfirmDelete()}
        titleText={messages.deleteCategoryDialogTitle}
        descriptionText={messages.deleteCategoryDialogContent}
        cancelText={messages.deleteCategoryDialogCancel}
        isLoading={isLoadingRemoveCategory}
        confirmText={messages.deleteCategoryDialogConfirm}
      />
    </Menu>
  );
}
